<template lang="pug">
SearchQuery
  template(#default="{ query, setQuery, sort, setSort, executeSearch, results }")
    .schedule-controls
      //- Search row: input + sort
      .schedule-controls__search-row
        .schedule-controls__search
          form.schedule-controls__form(@submit.prevent="executeSearch()")
            input.schedule-controls__input(
              :value="query"
              @input="setQuery($event)"
              @keypress.enter.prevent="executeSearch()"
              placeholder="Enter Search Term"
              type="text"
              name="schedule-search"
              autocomplete="off"
              aria-label="Search sessions")
            button.schedule-controls__search-clear(
              v-if="query"
              type="button"
              @click="onClear(setQuery, executeSearch)"
              aria-label="Clear search")
              span(aria-hidden="true") &times;
            button.schedule-controls__search-icon(type="submit" aria-label="Search")
              svg(viewBox="0 0 16 16" width="14" height="14")
                circle(cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="2" fill="none")
                line(x1="11" y1="11" x2="15" y2="15" stroke="currentColor" stroke-width="2")

        .schedule-controls__sort.schedule-controls__sort--desktop
          select.schedule-controls__sort-select(
            :value="sortValue(sort)"
            @change="onSortChange($event, results)"
            aria-label="Sort sessions")
            option(
              v-for="option in sortOptions(results)"
              :key="`${option.fieldName}_${option.sortMode}`"
              :value="`${option.fieldName}_${option.sortMode}`") {{ sortDisplayName(option) }}

      //- Mobile filter trigger
      button.schedule-controls__mobile-trigger(
        type="button"
        @click="drawerOpen = true") Filters

      //- Desktop filter dropdowns row
      .schedule-controls__filters.schedule-controls__filters--desktop
        .schedule-controls__filter(
          v-for="group in filterGroups"
          :key="group.id"
          :data-filter-id="group.id")
          button.schedule-controls__filter-trigger(
            type="button"
            @click="toggleGroup(group.id)"
            :class="{ 'schedule-controls__filter-trigger--active': activeCount(group) > 0 }"
            :aria-expanded="openGroupId === group.id ? 'true' : 'false'")
            | {{ triggerLabel(group) }}
            span.schedule-controls__filter-chevron(:class="{ 'schedule-controls__filter-chevron--open': openGroupId === group.id }")
          .schedule-controls__filter-panel(v-show="openGroupId === group.id")
            label.schedule-controls__filter-item(
              v-for="filter in group.filters"
              :key="filter.id")
              input(
                type="checkbox"
                :checked="filter.selected"
                @change="onFilterToggle(filter, executeSearch)")
              span {{ filter.value }} ({{ filter.count }})

      //- Mobile drawer
      .schedule-controls__drawer(
        v-if="drawerOpen"
        @click.self="drawerOpen = false")
        .schedule-controls__drawer-panel(@click.stop="")
          button.schedule-controls__drawer-close(
            type="button"
            @click="drawerOpen = false") Close
          .schedule-controls__drawer-section
            label.schedule-controls__drawer-section-label Sort
            select.schedule-controls__sort-select(
              :value="sortValue(sort)"
              @change="onSortChange($event, results)"
              aria-label="Sort sessions")
              option(
                v-for="option in sortOptions(results)"
                :key="`${option.fieldName}_${option.sortMode}`"
                :value="`${option.fieldName}_${option.sortMode}`") {{ sortDisplayName(option) }}
          .schedule-controls__drawer-section(v-for="group in filterGroups" :key="group.id")
            details
              summary {{ group.name }}
              label.schedule-controls__filter-item(
                v-for="filter in group.filters"
                :key="filter.id")
                input(
                  type="checkbox"
                  :checked="filter.selected"
                  @change="onFilterToggle(filter, executeSearch)")
                span {{ filter.value }} ({{ filter.count }})
          button.schedule-controls__drawer-apply(
            type="button"
            @click="drawerOpen = false") Apply
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ScheduleControls',

  data() {
    return {
      openGroupId: null,
      drawerOpen: false,
    };
  },

  computed: {
    ...mapGetters(['filters']),

    filterGroups() {
      if (!this.filters || !this.filters.length) return [];
      const ORDER = ['session_format', 'session_topics', 'session_credit_type'];
      return ORDER.map(id => this.filters.find(g => g.id === id)).filter(Boolean);
    },
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.onDrawerKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.onDrawerKeydown);
  },

  methods: {
    onClear(setQuery, executeSearch) {
      setQuery({ target: { value: '' } });
      executeSearch();
    },

    activeCount(group) {
      if (!group.filters) return 0;
      return group.filters.filter(f => f.selected).length;
    },

    triggerLabel(group) {
      return group.name;
    },

    toggleGroup(groupId) {
      this.openGroupId = this.openGroupId === groupId ? null : groupId;
    },

    onFilterToggle(filter, executeSearch) {
      this.$store.commit('setFilterValue', { filter, value: !filter.selected });
      this.$store.dispatch('executeSearch');
    },

    handleOutsideClick(event) {
      if (!this.$el || !this.openGroupId) return;
      const openEl = this.$el.querySelector(
        `.schedule-controls__filter[data-filter-id="${this.openGroupId}"]`
      );
      if (openEl && !openEl.contains(event.target)) {
        this.openGroupId = null;
      }
    },

    onDrawerKeydown(e) {
      if (e.key === 'Escape' && this.drawerOpen) this.drawerOpen = false;
    },

    sortValue(sort) {
      if (!sort) return 'startTime_asc';
      if (typeof sort === 'object') {
        const field = sort.fieldName || 'startTime';
        const mode = sort.sortMode || 'asc';
        return `${field}_${mode}`;
      }
      return sort;
    },

    sortOptions(results) {
      const SCHEDULE_SORT_KEYS = [
        'startTime_asc',
        'title_normalized_asc',
        'title_normalized_desc',
        'session_format_normalized_asc',
      ];
      const fallback = [
        { fieldName: 'startTime',                 sortMode: 'asc',  displayName: 'time' },
        { fieldName: 'title_normalized',          sortMode: 'asc',  displayName: 'a-z' },
        { fieldName: 'title_normalized',          sortMode: 'desc', displayName: 'z-a' },
        { fieldName: 'session_format_normalized', sortMode: 'asc',  displayName: 'session type' },
      ];
      if (results && results.sortOptions && results.sortOptions.length) {
        const filtered = results.sortOptions.filter(
          o => SCHEDULE_SORT_KEYS.includes(`${o.fieldName}_${o.sortMode}`)
        );
        return filtered.length ? filtered : fallback;
      }
      return fallback;
    },

    sortDisplayName(option) {
      if (!option) return '';
      const labels = {
        'startTime_asc':                 'Sort by time',
        'title_normalized_asc':          'Sort by a-z',
        'title_normalized_desc':         'Sort by z-a',
        'session_format_normalized_asc': 'Sort by session type',
      };
      return labels[`${option.fieldName}_${option.sortMode}`] || option.displayName || '';
    },

    onSortChange(event, results) {
      const value = event.target.value;
      const options = this.sortOptions(results);
      const selected = options.find(o => `${o.fieldName}_${o.sortMode}` === value);
      if (selected) {
        this.$store.dispatch('changeSort', selected);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

$lg-min: 1024px;

.schedule-controls {
  margin-bottom: 16px;
}

.schedule-controls__search-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  align-items: center;
}

.schedule-controls__search {
  flex: 1;
}

.schedule-controls__form {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid $color-border;
}

.schedule-controls__input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  font-family: 'Public Sans', sans-serif;
  font-weight: 400;
  line-height: 1.5;
  color: $color-text-dark;
  background: transparent;

  &::placeholder {
    color: $color-text-secondary;
  }
}

.schedule-controls__search-icon {
  background: none;
  border: none;
  cursor: pointer;
  color: $color-text-secondary;
  display: flex;
  align-items: center;
  padding: 0;
}

.schedule-controls__search-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: $color-text-secondary;
  display: flex;
  align-items: center;
  font-size: 18px;
  line-height: 1;
  padding: 0 8px;

  &:hover {
    color: $color-text-dark;
  }
}

.schedule-controls__sort {
  flex: 0 0 200px;
}

.schedule-controls__sort-select {
  width: 100%;
  height: 44px;
  padding: 10px 40px 10px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid $color-border;
  font-size: 14px;
  font-family: 'Public Sans', sans-serif;
  font-weight: 400;
  color: $color-text-dark;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%230F172A' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
}

.schedule-controls__filters {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
  position: relative;
}

.schedule-controls__filter {
  position: relative;
}

.schedule-controls__filter-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 10px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid $color-border-filter;
  font-size: 14px;
  font-family: 'Public Sans', sans-serif;
  font-weight: 400;
  color: $color-text-dark;
  cursor: pointer;
  white-space: nowrap;

  &--active {
    border-color: $color-primary;
    color: $color-primary;
  }
}

.schedule-controls__filter-chevron {
  display: inline-block;
  width: 10px;
  height: 6px;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%230F172A' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  transition: transform 0.15s ease;

  &--open {
    transform: rotate(180deg);
  }
}

.schedule-controls__filter-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 200px;
  background: white;
  border: 1px solid $color-border;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  z-index: 100;
  padding: 8px 0;
}

.schedule-controls__filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-family: 'Public Sans', sans-serif;
  color: $color-text-dark;
  cursor: pointer;

  &:hover {
    background: $color-surface-hover;
  }

  input[type="checkbox"] {
    cursor: pointer;
    flex-shrink: 0;
  }
}

@media (min-width: $lg-min) {
  .schedule-controls__mobile-trigger { display: none; }
  .schedule-controls__filters--desktop { display: flex; }
  .schedule-controls__drawer { display: none !important; }
  .schedule-controls__sort--desktop { display: block; }
  .schedule-controls__filter-panel { min-width: max-content; }
  .schedule-controls__filter-item span { white-space: nowrap; }
}

@media (max-width: $lg-min - 1) {
  .schedule-controls__mobile-trigger {
    display: block;
    width: 100%;
    height: 44px;
    background: white;
    border: 1px solid $color-primary;
    border-radius: 8px;
    margin-top: 12px;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    color: $color-text-dark;
    box-shadow: $shadow-btn;

    &:hover {
      background: $color-btn-outline-hover-bg;
      border-color: $color-btn-outline-hover-border;
    }

    &:focus-visible {
      outline: 2px solid $color-primary;
      outline-offset: 2px;
    }
  }
  .schedule-controls__filters--desktop { display: none; }
  .schedule-controls__sort--desktop { display: none; }
}

.schedule-controls__drawer {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 12000;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
}

.schedule-controls__drawer-panel {
  background: white;
  width: 320px;
  max-width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.schedule-controls__drawer-close {
  background: none;
  border: none;
  align-self: flex-end;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: $color-text-dark;
}

.schedule-controls__drawer-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.schedule-controls__drawer-section-label {
  font-size: 13px;
  font-weight: 600;
  color: $color-text-secondary;
}

.schedule-controls__drawer-apply {
  margin-top: auto;
  height: 44px;
  background: $color-cta-accent;
  color: $color-cta-text;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: $color-cta-accent-hover;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}
</style>
